// The context bar beside the scope picker (ZENITH.md §18, #254): one chip per
// facet the open page carries, naming what is selected and clearing it, each
// on its own — three facets are three chips (#263). Names are looked up
// live — the device from `GET /devices/{serial}`, through the one read the
// page shares (`device.tsx`, #1311), the network from
// `GET /networks/{id}` — and never stored, so a rename shows at once; an
// audit target, a station's address and an SSID are strings a page filters
// on, shown as they are — the address in its separated spelling.
//
// The same read is how a link lands: `/operate/devices?serial=X` with no site in the
// address opens on X's site, set from the device's `site_id` (and the site's
// customer and organization, read once). A serial the caller cannot see is
// a 404 like an absent one (§6.3), and the facet is dropped silently, as an
// unknown scope is dropped (#184) — the page says nothing about whether the
// serial exists. Any other failure keeps the facet and shows the identifier
// in the name's place: the read may succeed next time, and a device a cell
// did not answer for is not a device that does not exist.
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { SKIP, useQuery } from "../api/hooks";
import { mac } from "../ui";
import { useAddressedDevice, type AddressedDevice } from "./device";
import { facetSearch, facetsOf, facetValues, useFacets, type FacetName } from "./facets";
import { plural } from "../dashboard/format";
import { scopeSearch, useScope, type Scope } from "./scope";

const IDLE = { status: "idle" } as const;

export default function FacetBar() {
  const { facets } = useFacets();
  const { scope } = useScope();
  const location = useLocation();
  const navigate = useNavigate();
  // Only the facets this page understands are its context; one pasted onto
  // a page that does not read it is not shown as if it did something.
  const known = facetsOf(location.pathname);
  const has = (name: FacetName) => known.includes(name) ? facets[name] : undefined;
  const serial = has("serial"), network = has("network"), station = has("mac"), ssid = has("ssid"), targetType = has("target_type"), targetId = has("target_id");
  const ssids = ssid ? facetValues(location.search, "ssid") : [];

  // The device's read is the page's too (#1311): the shell holds one for the
  // serial the address names, and this is one of its readers.
  const device: AddressedDevice | { status: "idle"; data?: undefined } = useAddressedDevice(serial) ?? IDLE;
  const net = useQuery(() => network ? api.GET("/networks/{id}", { params: { path: { id: network } } }) : SKIP, [network]);
  // The device's site, when it is not the scope's: its customer and
  // organization are what the scope needs and the device record does not
  // carry. A network carries all three itself.
  const deviceSite = device.status === "ready" ? device.data.site_id : undefined;
  const wantSite = deviceSite !== undefined && deviceSite !== scope.siteId ? deviceSite : undefined;
  const site = useQuery(() => wantSite ? api.GET("/sites/{id}", { params: { path: { id: wantSite } } }) : SKIP, [wantSite]);

  // One address correction at a time, as a replace: the link the person
  // followed is the address they meant, and the corrected one stands in for
  // it rather than behind it in history.
  useEffect(() => {
    // A dropped device takes the open section of its page with it (#388).
    const drop = (name: FacetName) => navigate({ pathname: location.pathname, search: facetSearch({ ...facets, [name]: undefined, ...(name === "serial" ? { tab: undefined, port: undefined } : {}) }, location.search) }, { replace: true });
    const land = (next: Scope) => navigate({ pathname: location.pathname, search: scopeSearch(next, location.search) }, { replace: true });
    if (serial) {
      if (device.status === "error" && device.problem.reason === "not_found") { drop("serial"); return; }
      if (device.status === "ready" && !device.data.site_id) { drop("serial"); return; }
      if (wantSite) {
        if (site.status === "error" && site.problem.reason === "not_found") { drop("serial"); return; }
        if (site.status === "ready") { land({ orgId: site.data.org_id, customerId: site.data.customer_id, siteId: site.data.id }); return; }
      }
    }
    if (network) {
      if (net.status === "error" && net.problem.reason === "not_found") { drop("network"); return; }
      if (net.status === "ready" && net.data.site_id !== scope.siteId) { land({ orgId: net.data.org_id, customerId: net.data.customer_id, siteId: net.data.site_id }); }
    }
  }, [serial, network, device, site, net, wantSite, scope.siteId, facets, location.pathname, location.search, navigate]);

  const chips: { name: FacetName; kind: string; label: string; title?: string; clears: FacetName[] }[] = [];
  // Clearing the device clears the section of its page that was open too (#388).
  if (serial) chips.push({ name: "serial", kind: "Device", label: device.data?.name ?? serial, title: serial, clears: ["serial", "tab", "port"] });
  if (network) chips.push({ name: "network", kind: "Network", label: net.data?.name ?? network, title: network, clears: ["network"] });
  if (station) chips.push({ name: "mac", kind: "Client", label: mac(station), title: station, clears: ["mac"] });
  // Clients may name several SSIDs (#1602); the chip names them all and
  // clears them together, as the pickers on the page clear one.
  if (ssid) chips.push({ name: "ssid", kind: plural(ssids.length, "SSID", "SSIDs"), label: ssids.join(", "), clears: ["ssid"] });
  if (targetType || targetId) chips.push({ name: "target_type", kind: "Target", label: [targetType, targetId].filter(Boolean).join(" "), clears: ["target_type", "target_id"] });
  if (chips.length === 0) return null;
  const clear = (names: FacetName[]) => navigate({ pathname: location.pathname, search: facetSearch({ ...facets, ...Object.fromEntries(names.map((n) => [n, undefined])) }, location.search) });
  return (
    <span className="context" aria-label="Context">
      {chips.map((c) => (
        <span key={c.name} className="facet" title={c.title}>
          <span className="seg-kind">{c.kind}</span>{c.label}
          <button type="button" className="facet-clear" aria-label={`Clear ${c.kind.toLowerCase()}`} onClick={() => clear(c.clears)}>×</button>
        </span>
      ))}
    </span>
  );
}
